import React, { useContext, useEffect, useCallback, useMemo, useState } from 'react';
import { TableStateContext } from '../../contexts/tableState/TableStateContext';
import useApi from '../../utils/useApi';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import { endpoints } from '../../utils/endpoints';
import Table from './Table/Table';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Cell, Column, Row } from 'react-table';
import { NSTable } from '../../types/interfaces/NSTable';
import TablePaging from './TablePaging/TablePaging';
import { useCallbackOne, useMemoOne } from 'use-memo-one';
import useStyle from './TableWithState.style';
import { ITableData } from '../../types/interfaces/ITableData';
import { useHistory } from 'react-router-dom';
import { ETables } from '../../types/enums/ETables';
import { mutate } from 'swr';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { downloadFile } from '../../utils/downloadUtil';
import { config } from '../../constants/config';
import { themeColors } from '../../styles/themeColors';
import DeleteUserDialog from '../DeleteUserDialog/DeleteUserDialog';
import GenerateCodesDialog from './Popups/GenerateCodesDialog/GenerateCodesDialog';
import useSnackbar from '../../utils/useSnackbar';
import { ETablePopups } from '../../types/enums/ETablePopups';
import RemoveEsimsDialog from './Popups/RemoveEsimsDialog/RemoveEsimsDialog';
import { EStatus } from '../../types/enums/EStatus';
import _ from 'lodash';
import AnalyticsPackagesPopup from './Popups/AnalyticsPackagesHistory/AnalyticsPackagesHistory';
import { EPaths } from '../../types/enums/EPaths';

interface IProps {
  tableName: ETables;
  dataFormatter: (data: any) => NSTable.TData;
  cellStyleHandler?: (cell: Cell<any>) => any;
  columns: Column<any>[];
  exportable?: boolean;
  refreshable?: boolean;
  addable?: boolean;
  generateCodes?: boolean;
  removeEsims?: boolean;
  actions?: React.ReactNode | null;
}
interface IPropsDeleteUserPopUp {
  isOpen: boolean;
  user: object | null;
}

const TableWithState: React.FC<IProps> = ({
  tableName,
  dataFormatter,
  exportable,
  refreshable,
  addable,
  generateCodes = false,
  removeEsims = false,
  actions = null,
  ...rest
}) => {
  const styles = useStyle();

  const [dialog, setDialog] = useState<IPropsDeleteUserPopUp>({ isOpen: false, user: null });
  const [alanyticsIsOpen, setAnalyticsIsOpen] = useState<boolean>(false);

  const { setPage, initTableState, state, setPopup, setAllRowsIsChecked } = useContext(TableStateContext);

  const { t } = useTranslation();

  const snackbar = useSnackbar();

  const requestProps = useMemoOne(
    () => ({
      tableName,
      page: state[tableName]?.page || 1,
      sort: state[tableName]?.sorting,
      deleteId: state[tableName]?.deleting,
      filters: state[tableName]?.filtering,
    }),
    [state, tableName],
  );
  const hasCheckedRows = !!state[tableName]?.checkedIds?.length;
  const showPackagesActivationHandlers: boolean = tableName === ETables.Packages ? hasCheckedRows : false;
  const showPromocodeReservationHandlers: boolean = tableName === ETables.PromoCodes ? hasCheckedRows : false;
  const downloadAbusedPlans = tableName === ETables.Packages;
  const showAnalyticsPackagesXlsxHandlers: boolean = tableName === ETables.Packages;
  const showPartnerCodesActivationHandlers = tableName === ETables.PartnerCodes ? hasCheckedRows : false;

  const endpoint = endpoints.table(requestProps);

  const { data, get, remove, post, patch, isFetching, keyForSwr, mutate } = useApi<ITableData>(endpoint);

  const history = useHistory();

  const tableData = useMemo(() => data?.table && dataFormatter(data.table), [data, dataFormatter]);

  useEffect(() => initTableState(tableName), [initTableState, tableName]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(tableName, page);
    },
    [setPage, tableName],
  );

  const handleCellClick = useCallback(
    async (cell: Cell<any>) => {
      if (tableName === ETables.PromoCodes || _.includes(['jsxCheckbox', 'action'], cell.column.id)) return;

      const column = cell.column.Header;

      const tableItem = cell.row.original;

      if (column === 'Delete') setDialog({ isOpen: true, user: tableItem });
      else {
        let tN: string = tableName;
        if (tableName === ETables.InAppNotifications) tN = EPaths.InAppNotifications;
        history.push(`${tN}/${tableItem._id}`);
      }
    },

    [history, tableName],
  );

  const handleExportClick = useCallback(
    () =>
      get({
        endpoint: endpoints.exportTable(requestProps),
        onSuccess: ({ data }) => downloadFile(data, { name: tableName, extension: 'xlsx' }),
        options: {
          responseType: 'blob',
        },
      }),
    [get, requestProps, tableName],
  );

  const handleAbusedPlans = useCallback(
    () =>
      get({
        endpoint: endpoints.abusedPlans,
        onSuccess: ({ data }) =>
          downloadFile(data, { name: 'Abused "Invite a friend" ' + tableName, extension: 'xlsx' }),
        options: {
          responseType: 'blob',
        },
      }),
    [get, requestProps, tableName],
  );

  const handleRefreshClick = useCallback(async () => {
    const response = await get({ endpoint: endpoints.table({ ...requestProps, refresh: true }) });
    mutate(keyForSwr, response?.data);
  }, [get, keyForSwr, requestProps]);

  const handleOnAddClick = useCallback(() => {
    let tN: string = tableName;

    if (tableName === ETables.InAppNotifications) tN = EPaths.InAppNotifications;

    history.push(`${tN}/add`);
  }, [history, tableName]);

  const handleDeleteItem = useCallbackOne((tableName: string, itemId: string) => {
    remove({
      endpoint: endpoints.deleteTableItem(tableName, itemId),
      onSuccess: (r) => {
        mutate(keyForSwr);
      },
    });
  }, []);

  const onHandleGenerate = useCallbackOne(
    (amount: string, groupName: string, showInAdminPanel: boolean) => {
      post({
        endpoint: endpoints.generateActivationCodes,
        body: { amount: +amount, groupName, showInAdminPanel },
        onSuccess: () => {
          snackbar({ severity: 'success', message: t(tPaths.tableGenerateCodes.successOnAdd) });
          handleRefreshClick();
          setPopup(tableName, ETablePopups.GeneratePromocodes, { isOpen: false });
        },
        onError: (error) => {
          const constraints = _.get(error.response, 'data.errors[0].constraints', null);
          let message = '';

          if (constraints) message = `${constraints.min} ${constraints.max}`;
          else message = error.response?.data.message;

          snackbar({ severity: 'error', message });
          console.log('error:', error.response?.data.message);
        },
      });
    },
    [state],
  );

  const handlePackagesActivation = useCallbackOne(
    (status: EStatus) => {
      const idList: string[] = state[tableName].checkedIds;
      patch({
        endpoint: endpoints.updatePackagesStatus(idList),
        body: { status },
        onSuccess: () => {
          mutate();
          setAllRowsIsChecked(tableName, []);
        },
      });
    },
    [tableName, state, patch],
  );

  const handlePromocodeReservation = useCallbackOne(
    (status: EStatus) => {
      const idList: string[] = state[tableName].checkedIds;
      patch({
        endpoint: endpoints.reservePromocodes(idList),
        body: { status },
        onSuccess: () => {
          mutate();
          setAllRowsIsChecked(tableName, []);
        },
      });
    },
    [tableName, state, patch],
  );

  const handlePromocodeUnreservation = useCallbackOne(
    (status: EStatus) => {
      const idList: string[] = state[tableName].checkedIds;
      patch({
        endpoint: endpoints.unreservePromocodes(idList),
        body: { status },
        onSuccess: () => {
          mutate();
          setAllRowsIsChecked(tableName, []);
        },
      });
    },
    [tableName, state, patch],
  );

  const handlePartnerCodesActivation = useCallbackOne(
    (status: EStatus, changeStatusForAllCodesWithTheSameCampaign: boolean) => {
      const idList: string[] = state[tableName].checkedIds;
      patch({
        endpoint: endpoints.updatePartnerCodesStatus(idList, changeStatusForAllCodesWithTheSameCampaign),
        body: { status },
        onSuccess: () => {
          mutate();
          setAllRowsIsChecked(tableName, []);
        },
      });
    },
    [tableName, state, patch],
  );

  return (
    <Box className={styles.contentContainer}>
      {dialog.isOpen && (
        <DeleteUserDialog
          handleRemove={handleDeleteItem}
          setDialog={setDialog}
          user={dialog.user}
          tableName={tableName}
        />
      )}

      <GenerateCodesDialog
        isOpen={!!(state[tableName]?.popups ? state[tableName].popups[ETablePopups.GeneratePromocodes]?.isOpen : false)}
        tableName={tableName}
        onHandleGenerate={onHandleGenerate}
      />

      <AnalyticsPackagesPopup
        isOpen={alanyticsIsOpen}
        tableName={tableName}
        close={() => setAnalyticsIsOpen(false)}
        // onHandleGenerate={onHandleGenerate}
      />

      {removeEsims && <RemoveEsimsDialog tableName={tableName} refetchTable={() => mutate()} />}

      <Box className={styles.headerContainer}>
        <Box display="flex">
          <Typography variant="h4">{t(tPaths.table.table(tableName))}</Typography>
          <Box marginLeft={1} color={themeColors.textComplimentary}>
            <Typography variant="h4">{data?.total || '0'}</Typography>
          </Box>
        </Box>

        <Box marginLeft="auto" display="flex">
          {actions}

          {showAnalyticsPackagesXlsxHandlers && <Button onClick={() => setAnalyticsIsOpen(true)}>{'Analytics'}</Button>}

          {showPartnerCodesActivationHandlers && (
            <>
              <Button onClick={() => handlePartnerCodesActivation(EStatus.Active, false)} color="secondary">
                {t(tPaths.table.activate)}
              </Button>
              <Button
                onClick={() => handlePartnerCodesActivation(EStatus.Inactive, false)}
                color="secondary"
                style={{ marginLeft: 8 }}
              >
                {t(tPaths.table.deactivate)}
              </Button>
              <Button
                onClick={() => handlePartnerCodesActivation(EStatus.Active, true)}
                color="secondary"
                style={{ marginLeft: 8 }}
              >
                {t(tPaths.table.activateCampaign)}
              </Button>
              <Button
                onClick={() => handlePartnerCodesActivation(EStatus.Inactive, true)}
                color="secondary"
                style={{ marginLeft: 8 }}
              >
                {t(tPaths.table.deactivateCampaign)}
              </Button>
            </>
          )}

          {showPackagesActivationHandlers && (
            <>
              <Button onClick={() => handlePackagesActivation(EStatus.Active)} color="secondary">
                {t(tPaths.table.activateSelected)}
              </Button>
              <Button
                onClick={() => handlePackagesActivation(EStatus.Inactive)}
                color="secondary"
                style={{ marginLeft: 8 }}
              >
                {t(tPaths.table.deactivateSelected)}
              </Button>
            </>
          )}
          {showPromocodeReservationHandlers && (
            <>
              <Box marginLeft={1}>
                <Button onClick={() => handlePromocodeReservation(EStatus.Active)} color="secondary">
                  {t(tPaths.table.reservePromocode)}
                </Button>
              </Box>
              <Box marginLeft={1}>
                <Button onClick={() => handlePromocodeUnreservation(EStatus.Active)} color="secondary">
                  {t(tPaths.table.unreservePromocode)}
                </Button>
              </Box>
            </>
          )}

          {removeEsims && (
            <Box marginLeft={1}>
              <Button onClick={() => setPopup(tableName, ETablePopups.RemoveEsims, { isOpen: true })}>
                {t(tPaths.table.resetEsims)}
              </Button>
            </Box>
          )}

          {exportable && data && (
            <Box marginLeft={1}>
              <Button onClick={handleExportClick}>{t(tPaths.table.downloadXls)}</Button>
            </Box>
          )}

          {downloadAbusedPlans && (
            <Box marginLeft={1}>
              <Button onClick={handleAbusedPlans}>{t(tPaths.table.downloadAbusedPlans)}</Button>
            </Box>
          )}
          {refreshable && (
            <Box marginLeft={1}>
              <Button onClick={handleRefreshClick} color="secondary">
                {`${t(tPaths.table.refresh)} ${t(tPaths.table.table(tableName))}`}
              </Button>
            </Box>
          )}
          {addable && (
            <Box marginLeft={1}>
              <Button onClick={handleOnAddClick} color="primary" endIcon={<AddRoundedIcon />}>
                {t(tPaths.table.add)}
              </Button>
            </Box>
          )}
          {generateCodes && data && (
            <Box marginLeft={1}>
              <Button
                onClick={() => setPopup(tableName, ETablePopups.GeneratePromocodes, { isOpen: true })}
                color="secondary"
                endIcon={<AddRoundedIcon />}
              >
                {t(tPaths.table.generateCodes)}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {data && !isFetching ? (
        <>
          {data && (
            <Box className={styles.container}>
              <Table tableName={tableName} data={tableData} {...rest} handleCellClick={handleCellClick} />
              <TablePaging
                count={data.total as number}
                changePage={handlePageChange}
                rowsPerPage={config.itemsPerPage}
                page={state[tableName]?.page || 1}
              />
            </Box>
          )}
        </>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default TableWithState;
